import React, { useEffect, useState } from "react";
import {
  Grid,
  Typography,
  InputAdornment,
  TextField,
  Button,
  Paper,
  Box,
  Icon,
  Alert,
  MenuItem,
  Tooltip
} from "@mui/material";
import ApiList from "../../partials/ApiList";
import ResultField from "../../partials/ResultField";
import { SIG_FIGS, DISCLAIMER, CAPSULE_SIZES, EXCIPIENT_ERROR } from "../../../resources/constants";
import { numbersOnly } from "../../../utilities/strings";
import validate from "./validate";
import calculate from "./calculate";
import SelectField from '../../partials/SelectField';
import {TextFieldRightAligned} from '../../../utilities/theme';

/**
 * CapsuleTab
 * @param {*} props
 * @returns
 */
export default function CapsuleTab() {
  const [capsuleCount, setCapsuleCount] = useState(1);
  const [capsuleSize, setCapsuleSize] = useState("");
  const [excipientPackStat, setExcipientPackStat] = useState("");

  const [apiList, setApiList] = useState([{}]);
  const [results, setResults] = useState(null);
  const [errors, setErrors] = useState({});

  //useEffect: reset results if any variables change
  useEffect((_) => {
    setResults(null);
  }, [capsuleCount, capsuleSize, excipientPackStat, apiList]);

  const {urls} = JSON.parse(localStorage.getItem('FAGRONCALC_STORE') || '{}');
  const URL_PDF_PACKSTAT = (urls?.PDF_PACKSTAT ?? '');
  
  const getState = (_) => ({
    capsuleCount,
    capsuleSize,
    excipientPackStat,
    apiList,
  });

  //on button click
  const onCalculate = (_) => {
    const state = getState();
    if (validate(setErrors, state)) {
      calculate(setResults, state);
    }
  };

  const renderTitle = (
    <Grid container spacing={1} alignItems='center'>
      <Grid item md={8} py={2}>
        <Typography variant="h4">Capsules</Typography>
        <Typography variant="body1">
          Please supply the information below...
        </Typography>
      </Grid>
      <Grid md={4} py={2} justifyContent='flex-end' style={{display:'flex'}}>
        <Tooltip title="Common Capsule Excipient Packing Statistics">
          <Button
            variant="contained"
            color="primary"
            onClick={_ => { window.open(URL_PDF_PACKSTAT, "_blank") }}
            startIcon={<Icon className="fa fa-file-pdf-o" />}
          >
            Pack Stat Info
        </Button>
        </Tooltip>
      </Grid>
    </Grid>
  );

  const renderDisclaimer = (
    <Grid container spacing={1}>
      <Grid item xs={12} my={2}>
        <Alert severity="warning">{DISCLAIMER}</Alert>
      </Grid>
    </Grid>
  );

  const renderInputs = (
    <Grid container spacing={1}>
      <Grid item md={4}>
        <TextField
          my={1}
          required
          label="Number of Capsules"
          value={capsuleCount}
          onChange={(e) => setCapsuleCount(numbersOnly(e.target.value))}
          error={errors.capsuleCount}
          helperText={errors.capsuleCount}
          fullWidth
          {...TextFieldRightAligned}
        />
      </Grid>
      <Grid item md={4}>
        <SelectField
          required
          fullWidth
          label={"Capsule Size"}
          value={capsuleSize}
          onChange={(e) => setCapsuleSize(e.target.value)}
          my={1}
          error={errors.capsuleSize}
          helperText={errors.capsuleSize}
        >
          {CAPSULE_SIZES.map((size) => (
            <MenuItem value={size}>{size}</MenuItem>
          ))}
        </SelectField>
      </Grid>
      <Grid item md={4}>
        <TextField
          required
          my={1}
          label="Excipient Pack Stat"
          value={excipientPackStat}
          onChange={(e) => setExcipientPackStat(numbersOnly(e.target.value, true))}
          error={errors.excipientPackStat}
          helperText={errors.excipientPackStat}
          fullWidth
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <Typography>mg</Typography>
              </InputAdornment>
            ),
          }}
          {...TextFieldRightAligned}
        />
      </Grid>
    </Grid>
  );

  const renderApiList = (
    <Grid container spacing={1} py={1}>
      <Grid item xs>
        <ApiList
          capsules
          data={apiList}
          errors={errors.apiList}
          results={results?.apiList}
          countRange = {{ min: 0, max: 7 }}
          onChange={setApiList}
        />
      </Grid>
    </Grid>
  );

  const renderButton = (
    <Grid container spacing={1} py={1}>
      <Grid item xs>
        <Button
          variant="contained"
          color="primary"
          endIcon={<Icon className="fa fa-paper-plane" />}
          onClick={onCalculate}
        >
          Calculate
        </Button>
      </Grid>
    </Grid>
  );

  const renderResults = results && (
    <Grid container spacing={1}>
      <Grid item xs my={2}>
        <Paper variant="secondary">
          <Box p={2}>
            <Typography variant="h5">Results</Typography>
            <ResultField label="Capsule Size">
              {capsuleSize}
            </ResultField>
            {true && (
              <ResultField label="Excipient Per Capsule">
                {results.excipientCapsule.toFixed(SIG_FIGS)} mg
              </ResultField>
            )}
            <ResultField label="Excipient Quantity">
              {results.excipientTotal > 0 ?
                `${results.excipientTotal.toFixed(SIG_FIGS)} g` :
                <Alert severity="error">
                  {EXCIPIENT_ERROR}
                </Alert>
              }
            </ResultField>
          </Box>
        </Paper>
      </Grid>
    </Grid>
  );

  return (
    <Grid container spacing={1}>
      {renderTitle}
      {renderInputs}
      {renderApiList}
      {renderButton}
      {renderResults}
      {renderDisclaimer}
    </Grid>
  );
}
